import classNames from 'classnames';
import { formatCents, PriceBreakdown, PriceLine } from '../../../lib/checkout/pricing';
import styles from './OrderSummary.module.scss';

export interface OrderSummaryProps {
  breakdown: PriceBreakdown;
  /** Sets the total large, for the desktop checkout's summary column where it is the headline figure. */
  largeTotal?: boolean;
}

/** What goes in the amount column. A credit is signed rather than coloured alone, so it reads
 *  correctly in mono too; "Free" and "Included" say the zero in words rather than as "$0". */
function amountText(line: PriceLine): string {
  if (line.isIncluded) {
    return 'Included';
  }
  if (line.isFree) {
    return 'Free';
  }
  return `${line.isCredit ? '−' : ''}${formatCents(line.amountInCents)}`;
}

/**
 * The itemised total, shared by the bag and the checkout screen.
 *
 * Purely presentational: every figure comes from `PriceBreakdown`, which is built from server-side
 * pricing, so this component has no arithmetic of its own and cannot drift from what will be charged.
 * The prototype shows the same total on both screens on purpose ("same total, third time they've seen
 * it"), which is why this is one component rather than two.
 */
export default function OrderSummary({ breakdown, largeTotal = false }: OrderSummaryProps) {
  return (
    <div className={styles.summary}>
      {breakdown.lines.map((line) => {
        return (
          <div className={styles.row} key={line.label}>
            {/* the credit's label is green as well as its amount, so the line reads as money taken off */}
            <span className={classNames(styles.label, { [styles.creditLabel]: line.isCredit })}>{line.label}</span>
            {/* the two lines that reduce what is owed carry the same colour, so the reason the total
                is $20 reads as one idea rather than two unrelated green words */}
            <span className={classNames(styles.amount, { [styles.credit]: line.isCredit || line.isFree })}>
              {line.valueInCents ? <s className={styles.value}>{formatCents(line.valueInCents)}</s> : null}
              {amountText(line)}
            </span>
          </div>
        );
      })}

      <div className={classNames(styles.row, styles.totalRow, { [styles.largeTotal]: largeTotal })}>
        <span className={styles.totalLabel}>Total today</span>
        <span className={styles.totalAmount}>{formatCents(breakdown.totalInCents)}</span>
      </div>
    </div>
  );
}
